import { useState, useEffect } from 'react';
import { Plus, Award, ExternalLink, RefreshCw, Trash2, Edit2, Link as LinkIcon, Image as ImageIcon, Loader2 } from 'lucide-react';
import { getCertifications, createCertification, updateCertification, deleteCertification, importCredlyBadge } from '../services/certificationsApi';
import type { Certification } from '../services/certificationsApi';

export function AdminCertifications() {
    const [certs, setCerts] = useState<Certification[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [credlyUrl, setCredlyUrl] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        issuer: '',
        type: 'certification' as 'certification' | 'badge',
        date: new Date().toISOString().split('T')[0],
        imageUrl: '',
        url: '',
        description: '',
        credentialId: ''
    });

    useEffect(() => {
        fetchCerts();
    }, []);

    const fetchCerts = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getCertifications();
            setCerts(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement des certifications');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (cert?: Certification) => {
        if (cert) {
            setShowForm(true);
            setEditingId(cert.id);
            setFormData({
                title: cert.title,
                issuer: cert.issuer,
                type: cert.type || 'certification',
                date: new Date(cert.date).toISOString().split('T')[0],
                imageUrl: cert.imageUrl,
                url: cert.url,
                description: cert.description,
                credentialId: cert.credentialId
            });
        } else {
            setShowForm(true);
            setEditingId(null);
            setFormData({
                title: '',
                issuer: '',
                type: 'certification',
                date: new Date().toISOString().split('T')[0],
                imageUrl: '',
                url: '',
                description: '',
                credentialId: ''
            });
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingId(null);
        setCredlyUrl(''); // Clear Credly URL when closing form
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError(null);

            const certData = {
                ...formData,
                date: new Date(formData.date).getTime()
            };

            if (editingId) {
                await updateCertification(editingId, certData);
            } else {
                await createCertification(certData);
            }

            await fetchCerts();
            setShowForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImportCredly = async () => {
        if (!credlyUrl || !credlyUrl.startsWith('https://www.credly.com/badges/')) {
            setError('Veuillez entrer une URL Credly valide.');
            return;
        }

        setIsImporting(true);
        setError('');

        try {
            const data = await importCredlyBadge(credlyUrl);
            setFormData(prev => ({
                ...prev,
                title: data.title || prev.title,
                issuer: data.issuer || prev.issuer,
                imageUrl: data.imageUrl || prev.imageUrl,
                url: data.url || prev.url,
                credentialId: data.credentialId || prev.credentialId,
                type: data.type === 'badge' ? 'badge' : 'certification',
                // If a date string was returned, try to keep it, otherwise keep current
                date: data.date ? new Date(data.date).toISOString().split('T')[0] : prev.date
            }));
            setCredlyUrl(''); // clear on success
            // Optionally, show a temporary success message
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de l\'import Credly');
        } finally {
            setIsImporting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette certification ?')) return;

        try {
            setLoading(true);
            await deleteCertification(id);
            await fetchCerts();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
            setLoading(false);
        }
    };

    if (loading && certs.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#1f75cb]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-[#1d1d1f] flex items-center gap-2">
                    <Award className="w-8 h-8 text-[#fc6d26]" />
                    Certifications & Badges
                </h1>
                <button
                    onClick={() => handleOpenForm()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Ajouter
                </button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-100">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">
                            {editingId ? 'Modifier la certification' : 'Nouvelle certification'}
                        </h2>

                        {/* Import Credly Section */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <Award className="w-4 h-4 text-[#1f75cb]" />
                                Import Rapide depuis Credly
                            </h3>
                            <div className="flex gap-3">
                                <input
                                    type="url"
                                    placeholder="https://www.credly.com/badges/..."
                                    value={credlyUrl}
                                    onChange={(e) => setCredlyUrl(e.target.value)}
                                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                />
                                <button
                                    onClick={handleImportCredly}
                                    disabled={!credlyUrl || isImporting || !credlyUrl.startsWith('https://www.credly.com/badges/')}
                                    className={`px-4 py-2 bg-[#1f75cb] text-white rounded-lg flex items-center justify-center font-medium
                                        ${(!credlyUrl || isImporting || !credlyUrl.startsWith('https://www.credly.com/badges/')) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#1f75cb]/90 transition-colors'}`}
                                >
                                    {isImporting ? (
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                    ) : (
                                        'Importer'
                                    )}
                                </button>
                            </div>
                            {credlyUrl && !credlyUrl.startsWith('https://www.credly.com/badges/') && (
                                <p className="text-xs text-orange-500 mt-2">L'URL doit commencer par "https://www.credly.com/badges/"</p>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="AWS Solutions Architect"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Émetteur *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.issuer}
                                        onChange={e => setFormData({ ...formData, issuer: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Amazon Web Services"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                                <select
                                    value={formData.type}
                                    onChange={e => setFormData({ ...formData, type: e.target.value as 'certification' | 'badge' })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                >
                                    <option value="certification">Certification</option>
                                    <option value="badge">Badge</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={e => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">ID de certification</label>
                                    <input
                                        type="text"
                                        value={formData.credentialId}
                                        onChange={e => setFormData({ ...formData, credentialId: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="ABCD1234EFGH"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <ImageIcon className="w-4 h-4 inline-block mr-1 text-gray-400" />
                                    URL de l'image / badge
                                </label>
                                <input
                                    type="url"
                                    value={formData.imageUrl}
                                    onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="https://..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <LinkIcon className="w-4 h-4 inline-block mr-1 text-gray-400" />
                                    URL de vérification
                                </label>
                                <input
                                    type="url"
                                    value={formData.url}
                                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="https://credly.com/..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20 min-h-[100px]"
                                    placeholder="Compétences acquises..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {certs.length === 0 && !loading && !error ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                    <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune certification pour le moment</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {certs.map(cert => (
                        <div key={cert.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col relative group">
                            <div className="absolute top-4 right-16 px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 capitalize">
                                {cert.type || 'certification'}
                            </div>
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleOpenForm(cert)}
                                    className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
                                    title="Modifier"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(cert.id)}
                                    className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100"
                                    title="Supprimer"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="w-16 h-16 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden mb-4 shrink-0">
                                {cert.imageUrl ? (
                                    <img src={cert.imageUrl} alt={cert.title} className="w-full h-full object-contain" />
                                ) : (
                                    <Award className="w-8 h-8 text-gray-400" />
                                )}
                            </div>

                            <div className="flex-1">
                                <h3 className="font-bold text-gray-900 leading-tight mb-1 pr-16">{cert.title}</h3>
                                <p className="text-sm text-gray-500 mb-2">{cert.issuer}</p>

                                {cert.credentialId && (
                                    <p className="text-xs text-gray-400 font-mono mb-2">ID: {cert.credentialId}</p>
                                )}

                                {cert.description && (
                                    <p className="text-sm text-gray-600 line-clamp-2 mt-2">{cert.description}</p>
                                )}
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between text-xs text-gray-500">
                                <span>{new Date(cert.date).toLocaleDateString()}</span>
                                {cert.url && (
                                    <a
                                        href={cert.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-[#1f75cb] hover:underline"
                                    >
                                        Vérifier <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
